import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { InstructorConsentStatusResponse } from "./contracts/InstructorConsentStatusResponse";
import {
  fetchConsentStatus,
  acceptConsent,
} from "./slice-extensions/instructor-consent-slice-extension";

interface InstructorConsentSliceState {
  status?: InstructorConsentStatusResponse;
  loadingStatus: LoadingStatus;
  acceptStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: InstructorConsentSliceState = {
  status: undefined,
  loadingStatus: LoadingStatus.Idle,
  acceptStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const setStatus = (
  state: InstructorConsentSliceState,
  action: PayloadAction<InstructorConsentStatusResponse>
) => {
  state.status = action.payload;
};

const instructorConsentSlice = createSlice({
  name: "instructorConsent",
  initialState,
  reducers: {
    clearConsentError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsentStatus.pending, (state) => {
        state.loadingStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchConsentStatus.fulfilled, (state, action) => {
        state.loadingStatus = LoadingStatus.Idle;
        setStatus(state, action);
      })
      .addCase(fetchConsentStatus.rejected, (state, action) => {
        state.loadingStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(acceptConsent.pending, (state) => {
        state.acceptStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(acceptConsent.fulfilled, (state, action) => {
        state.acceptStatus = LoadingStatus.Idle;
        setStatus(state, action);
      })
      .addCase(acceptConsent.rejected, (state, action) => {
        state.acceptStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearConsentError } = instructorConsentSlice.actions;

export const selectConsentStatus = (state: RootState) =>
  state.instructorConsent.status;
export const selectConsentLoadingStatus = (state: RootState) =>
  state.instructorConsent.loadingStatus;
export const selectConsentAcceptStatus = (state: RootState) =>
  state.instructorConsent.acceptStatus;
export const selectConsentError = (state: RootState) =>
  state.instructorConsent.lastError;

export default instructorConsentSlice.reducer;
